/**
 * The friends panel as a right-side sheet.
 *
 * Radix's `Dialog` rather than a hand-rolled drawer, for the parts that are easy
 * to get wrong and invisible when they are: the focus trap, the inert
 * background, Escape, and the aria wiring. Only the geometry is ours.
 *
 * Not the shared `<Dialog/>`: that one is a centred modal, and this has to sit
 * against the right edge and run the full height, because it is a list you scan
 * while the page behind it stays recognisable.
 */

import * as RadixDialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import { Button } from '../../ui/Button';

import { FriendsPanel, type FriendsPanelProps } from './FriendsPanel';
import styles from './FriendsPanel.module.css';

export interface FriendsSheetProps extends FriendsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FriendsSheet({ open, onOpenChange, onInvite, onInvited }: FriendsSheetProps) {
  const { t } = useTranslation('social');

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay} />
        <RadixDialog.Content className={styles.sheet} data-testid="friends-sheet">
          <div className={styles.sheetHead}>
            <RadixDialog.Title className={styles.sheetTitle}>{t('title')}</RadixDialog.Title>
            <RadixDialog.Close asChild>
              <Button size="sm" variant="ghost" data-testid="friends-close">
                {t('close')}
              </Button>
            </RadixDialog.Close>
          </div>
          <RadixDialog.Description className="visually-hidden">
            {t('title')}
          </RadixDialog.Description>

          <FriendsPanel
            {...(onInvite === undefined ? {} : { onInvite })}
            {...(onInvited === undefined ? {} : { onInvited })}
          />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
